import { useState, useMemo } from 'react'
import { X } from 'lucide-react'
import { BLOCK_TYPES } from '../db/database'
import useTaskStore from '../store/taskStore'
import { DateTimePicker } from './DateTimePicker'
import { useT, useLang } from '../i18n/useT'

// Helpers local time ↔ ISO (condivisi con pagina Blocchi)
export function tsToParts(iso) {
  const d = new Date(iso)
  const pad = n => String(n).padStart(2, '0')
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}
export function partsToTs(dateStr, timeStr) {
  const d = new Date(`${dateStr}T${timeStr}:00`)
  return d.toISOString()
}

const todayISOStr = () => new Date().toISOString().split('T')[0]

// Monday = 0, Sunday = 6
const jsToMon = (js) => (js + 6) % 7
const DEFAULT_WEEKDAYS = [0, 1, 2, 3, 4] // Lun..Ven

// Preset weekday selections
const WEEKDAY_PRESETS = [
  { key: 'workdays', days: [0, 1, 2, 3, 4] },
  { key: 'weekend',  days: [5, 6] },
  { key: 'all',      days: [0, 1, 2, 3, 4, 5, 6] },
]

const arraysEqual = (a, b) => {
  if (a.length !== b.length) return false
  const sa = [...a].sort(), sb = [...b].sort()
  return sa.every((v, i) => v === sb[i])
}

const buildEmptyForm = (preset) => {
  const p = preset ?? {}
  const sd = p.startDate ?? todayISOStr()
  // Termina di default 12 settimane dopo startDate
  const d = new Date(`${sd}T00:00:00`)
  d.setDate(d.getDate() + 12 * 7)
  const pad = n => String(n).padStart(2, '0')
  const termDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  return {
    type: p.type ?? 'Lavoro',
    label: p.label ?? '',
    startDate: sd,
    startTime: p.startTime ?? '09:00',
    endDate:   p.endDate ?? p.startDate ?? sd,
    endTime:   p.endTime ?? '18:00',
    recurrence: 'once',
    weekdays: [...DEFAULT_WEEKDAYS],
    termDate,
  }
}

// Clamp: end datetime deve essere sempre > start datetime.
// Se non lo è, porta end a start + 1h.
function clampEnd(f) {
  const s = new Date(`${f.startDate}T${f.startTime}:00`)
  const e = new Date(`${f.endDate}T${f.endTime}:00`)
  if (e > s) return f
  const adj = new Date(s.getTime() + 60 * 60 * 1000)
  const pad = n => String(n).padStart(2, '0')
  return {
    ...f,
    endDate: `${adj.getFullYear()}-${pad(adj.getMonth() + 1)}-${pad(adj.getDate())}`,
    endTime: `${pad(adj.getHours())}:${pad(adj.getMinutes())}`,
  }
}

// `editing` → oggetto blocco esistente (modalità modifica).
// `preset`  → default per creazione (es. data scelta dal calendario).
// `onSaveOverride` → se presente, sostituisce il salvataggio su DB: al
//    submit chiama `onSaveOverride({ type, label, startAt, endAt })` e
//    lascia al chiamante la gestione (es. mutare un batch in memoria).
export default function BloccoForm({ editing, preset, onClose, onSaveOverride }) {
  const t    = useT()
  const lang = useLang()
  const { addBlocco, updateBlocco } = useTaskStore()

  const [form, setForm] = useState(() => {
    if (editing) {
      const s = tsToParts(editing.startAt)
      const e = tsToParts(editing.endAt)
      // Termina di default 12 settimane dopo startDate (non modificabile in edit)
      const d = new Date(`${s.date}T00:00:00`)
      d.setDate(d.getDate() + 12 * 7)
      const pad = n => String(n).padStart(2, '0')
      return {
        type: editing.type,
        label: editing.label || '',
        startDate: s.date,
        startTime: s.time,
        endDate:   e.date,
        endTime:   e.time,
        recurrence: editing.recurrence || 'once',
        weekdays: [...DEFAULT_WEEKDAYS],
        termDate: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      }
    }
    return buildEmptyForm(preset)
  })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  // Tutti gli handler di inizio/fine passano da clampEnd per garantire
  // ordine cronologico end > start.
  const updateStartDate = (d)  => setForm(f => clampEnd({ ...f, startDate: d }))
  const updateStartTime = (tm) => setForm(f => clampEnd({ ...f, startTime: tm }))
  const updateEndDate   = (d)  => setForm(f => clampEnd({ ...f, endDate: d }))
  const updateEndTime   = (tm) => setForm(f => clampEnd({ ...f, endTime: tm }))

  const toggleWeekday = (idx) => setForm(f => {
    const has = f.weekdays.includes(idx)
    const next = has ? f.weekdays.filter(x => x !== idx) : [...f.weekdays, idx]
    // Almeno un giorno deve essere selezionato
    if (next.length === 0) return f
    return { ...f, weekdays: next.sort((a, b) => a - b) }
  })
  const applyWeekdayPreset = (days) => setForm(f => ({ ...f, weekdays: [...days] }))

  const handleSubmit = async (ev) => {
    ev.preventDefault()
    if (saving) return
    setError('')
    const startAt = partsToTs(form.startDate, form.startTime)
    const endAt   = partsToTs(form.endDate,   form.endTime)
    if (new Date(endAt) <= new Date(startAt)) {
      setError(t('block_err_invalid_range'))
      return
    }

    // Override hook: chiamante gestisce il salvataggio (es. modifica di un
    // record in un batch non ancora persistito, come l'import da immagine).
    if (onSaveOverride) {
      onSaveOverride({ type: form.type, label: form.label, startAt, endAt })
      onClose()
      return
    }

    setSaving(true)
    try {
      if (editing) {
        await updateBlocco(editing.id, {
          type: form.type, label: form.label, startAt, endAt,
        })
      } else {
        await addBlocco({
          type: form.type,
          label: form.label,
          startAt, endAt,
          recurrence: form.recurrence,
          weekdays: form.recurrence === 'weekly' ? form.weekdays : undefined,
          // Termina sempre ad una data specifica scelta dall'utente
          terminationMode: 'until',
          terminationUntil: partsToTs(form.termDate, '23:59'),
        })
      }
      onClose()
    } catch (e) {
      setError(e.message || 'Errore')
      setSaving(false)
    }
  }

  // Summary sentence per il banner arancione
  const summary = useMemo(() => {
    if (form.recurrence !== 'weekly' || form.weekdays.length === 0) return null
    const dayShort = lang === 'it'
      ? ['lun','mar','mer','gio','ven','sab','dom']
      : ['mon','tue','wed','thu','fri','sat','sun']
    const sel = form.weekdays.map(i => dayShort[i]).join(', ')
    return t('block_summary_sentence', {
      days: sel,
      start: form.startTime,
      end: form.endTime,
    })
  }, [form.recurrence, form.weekdays, form.startTime, form.endTime, lang, t])

  const weekdayShort = lang === 'it'
    ? ['L', 'M', 'M', 'G', 'V', 'S', 'D']
    : ['M', 'T', 'W', 'T', 'F', 'S', 'S']

  return (
    <div
      className="modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="modal-sheet" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        <div className="modal-header">
          <div className="block-form-title-wrap">
            <span className="modal-title">
              {editing ? t('block_edit_title') : t('block_new_title')}
            </span>
            <div className="block-form-subtitle">{t('block_new_sub')}</div>
          </div>
          <button className="btn-icon" onClick={onClose} aria-label={t('block_cancel')}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>

            {/* TIPO */}
            <div className="form-group">
              <label className="block-form-section-label">{t('block_type')}</label>
              <div className="block-type-grid">
                {Object.entries(BLOCK_TYPES).map(([k, meta]) => {
                  const active = form.type === k
                  return (
                    <button
                      key={k}
                      type="button"
                      className={`block-type-pill${active ? ' active' : ''}`}
                      style={active ? { background: meta.bg, color: meta.fg, borderColor: meta.stripe } : undefined}
                      onClick={() => setForm(f => ({ ...f, type: k }))}
                    >
                      <meta.Icon size={13} strokeWidth={2.2} />
                      {t(`block_type_${k.toLowerCase()}`)}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* ETICHETTA */}
            <div className="form-group">
              <label className="block-form-section-label">
                {t('block_label')}
                <span className="block-form-section-optional"> ({t('block_label_optional')})</span>
              </label>
              <input
                type="text"
                value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder={t('block_label_ph')}
                maxLength={100}
              />
            </div>

            {/* INIZIO */}
            <div className="form-group">
              <label className="block-form-section-label">{t('block_start')}</label>
              <DateTimePicker
                value={form.startDate}
                timeValue={form.startTime}
                onDateChange={updateStartDate}
                onTimeChange={updateStartTime}
                lang={lang}
              />
            </div>

            {/* FINE */}
            <div className="form-group">
              <label className="block-form-section-label">{t('block_end')}</label>
              <DateTimePicker
                value={form.endDate}
                timeValue={form.endTime}
                onDateChange={updateEndDate}
                onTimeChange={updateEndTime}
                lang={lang}
                minISO={form.startDate}
              />
            </div>

            {/* RICORRENZA (solo in creazione, no edit) */}
            {!editing && (() => {
              const isOnce = form.recurrence === 'once'
              return (
                <>
                  <div className="form-group">
                    <label className="block-form-section-label">{t('block_recurrence')}</label>
                    <div className="block-recurrence-row">
                      <button
                        type="button"
                        className={`block-type-pill${isOnce ? ' active' : ''}`}
                        onClick={() => setForm(f => ({ ...f, recurrence: 'once' }))}
                      >
                        {t('block_recurrence_once')}
                      </button>
                      <button
                        type="button"
                        className={`block-type-pill${!isOnce ? ' active' : ''}`}
                        onClick={() => setForm(f => ({ ...f, recurrence: 'weekly' }))}
                      >
                        {t('block_recurrence_weekly')}
                      </button>
                    </div>
                  </div>

                  {/* Weekly section: sempre montata, disabled se recurrence='once' */}
                  <div
                    className={`block-weekly-section${isOnce ? ' disabled' : ''}`}
                    aria-disabled={isOnce}
                  >
                    {/* Weekday selector: 7 chip circolari */}
                    <div className="weekday-chips">
                      {weekdayShort.map((label, idx) => {
                        const active = form.weekdays.includes(idx)
                        const weekend = idx >= 5
                        return (
                          <button
                            key={idx}
                            type="button"
                            disabled={isOnce}
                            className={`weekday-chip${active ? ' active' : ''}${weekend ? ' weekend' : ''}`}
                            onClick={() => toggleWeekday(idx)}
                            aria-label={label}
                            aria-pressed={active}
                          >
                            {label}
                          </button>
                        )
                      })}
                    </div>

                    {/* Preset rapidi (Lavorativi/Weekend/Tutti) */}
                    <div className="weekday-presets">
                      {WEEKDAY_PRESETS.map(p => {
                        const active = arraysEqual(form.weekdays, p.days)
                        return (
                          <button
                            key={p.key}
                            type="button"
                            disabled={isOnce}
                            className={`weekday-preset${active ? ' active' : ''}`}
                            onClick={() => applyWeekdayPreset(p.days)}
                          >
                            {t(`block_weekday_preset_${p.key}`)}
                          </button>
                        )
                      })}
                    </div>

                    {/* TERMINA: solo date picker allineato agli altri */}
                    <div className="form-group">
                      <label className="block-form-section-label">{t('block_termina')}</label>
                      <DateTimePicker
                        value={form.termDate}
                        timeValue=""
                        onDateChange={v => setForm(f => ({ ...f, termDate: v }))}
                        onTimeChange={() => {}}
                        lang={lang}
                        minISO={form.startDate}
                        hideTime
                      />
                    </div>
                  </div>
                </>
              )
            })()}

            {error && <div className="form-error" style={{ marginTop: 4 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'stretch' }}>
              <button type="button" className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>
                {t('block_cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ flex: 1, marginTop: 0 }}
              >
                {saving ? '…' : t('block_save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
